/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RotateCcw, ChevronRight, Loader2, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { statusLabel } from './EstimateStatusSelect';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pretty "N min ago" for the audit row's right column.
function relativeTime(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = (Date.now() - then) / 1000; // seconds
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// Sentence-render an event row: "set Foo to Confirmed (was Draft)" or
// "cleared override on Foo (was Confirmed)". Keeps the row compact while
// still surfacing the before/after delta so the Undo target is obvious.
function describeEvent(evt) {
  if (evt.event === 'workflow_status_cleared') {
    return evt.previous_status
      ? <>cleared override <span className="text-slate-400">(was <strong>{statusLabel(evt.previous_status)}</strong>)</span></>
      : <>cleared override</>;
  }
  const next = statusLabel(evt.status);
  const prev = evt.previous_status ? statusLabel(evt.previous_status) : 'auto';
  return <>set status to <strong className="text-slate-700">{next}</strong> <span className="text-slate-400">(was {prev})</span></>;
}

/**
 * Compact audit feed of recent workflow-status overrides. Renders as a
 * History icon button in the Workspace Dashboard header; clicking opens
 * a Popover with the most recent 10 events tenant-wide (or own-scope for
 * non-privileged roles).
 *
 * Props:
 *   - onChange(): fired AFTER a successful Undo so the parent can refetch
 *     the workspaces list (status chips need to reflect the revert).
 */
export default function StatusOverrideAuditFeed({ onChange }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoingId, setUndoingId] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/estimates/status-overrides/recent?limit=10`,
        { withCredentials: true },
      );
      setEvents(r.data?.events || []);
    } catch (e) {
      logger.warn('status-override audit fetch failed', e?.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch every time the popover opens (rather than polling) — small
  // payload, no need to keep a stream open. Also exposes a refresh on
  // re-open after an Undo so the list reflects the new latest event.
  useEffect(() => {
    if (open) fetchEvents();
  }, [open, fetchEvents]);

  const handleUndo = useCallback(async (evt) => {
    if (!evt.can_undo || undoingId) return;
    setUndoingId(evt.id);
    try {
      await axios.post(
        `${API_URL}/api/estimates/status-overrides/${evt.id}/undo`,
        {},
        { withCredentials: true },
      );
      const what = evt.event === 'workflow_status_cleared' ? 'Re-applied override' : 'Reverted status';
      toast.success(`${what} on ${evt.estimate_name}`);
      // Refresh the feed and let parent know to refresh workspace list.
      await fetchEvents();
      onChange && onChange();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) {
        toast.error('Cannot undo — a newer status change exists. Refreshing the feed.');
        await fetchEvents();
      } else {
        toast.error(e?.response?.data?.detail || 'Undo failed');
      }
    } finally {
      setUndoingId(null);
    }
  }, [undoingId, fetchEvents, onChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="status-audit-feed-trigger"
          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#0F2040] hover:bg-slate-100 px-2 py-1 rounded-md transition-colors border border-slate-200"
          title="Recent status changes"
        >
          <History className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Activity</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0"
        data-testid="status-audit-feed-popover"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <History className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-semibold text-[#0F2040]">Recent status changes</span>
            {events.length > 0 && (
              <Badge variant="secondary" className="text-[9px]">{events.length}</Badge>
            )}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-600"
            data-testid="status-audit-feed-close"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          ) : events.length === 0 ? (
            <div className="py-8 text-center text-[11px] text-slate-400">
              No recent status changes.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="status-audit-feed-list">
              {events.map((evt) => (
                <li
                  key={evt.id}
                  className="px-3 py-2 hover:bg-slate-50/60 group"
                  data-testid={`status-audit-feed-row-${evt.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-600 leading-snug">
                        <span className="font-medium text-[#0F2040]">{evt.by_name || 'Someone'}</span>{' '}
                        {describeEvent(evt)}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={evt.estimate_name}>
                        on <span className="text-slate-500">{evt.estimate_name}</span>
                        {evt.organization_name && <span className="text-slate-400"> · {evt.organization_name}</span>}
                      </div>
                      {evt.note && (
                        <div className="text-[10px] text-slate-500 italic mt-0.5 truncate" title={evt.note}>
                          “{evt.note}”
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">{relativeTime(evt.at)}</span>
                      {evt.can_undo ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUndo(evt)}
                          disabled={undoingId === evt.id}
                          className="h-6 px-1.5 text-[10px] text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`status-audit-feed-undo-${evt.id}`}
                          title="Revert this change"
                        >
                          {undoingId === evt.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><RotateCcw className="w-3 h-3 mr-0.5" /> Undo</>}
                        </Button>
                      ) : (
                        <span
                          className="text-[9px] text-slate-300"
                          title="A newer change exists; undo only works on the latest event for an estimate"
                          data-testid={`status-audit-feed-superseded-${evt.id}`}
                        >
                          superseded
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {events.length > 0 && (
          <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">Most recent first</span>
            <button
              type="button"
              onClick={() => fetchEvents()}
              className="text-[10px] text-slate-500 hover:text-[#0F2040] inline-flex items-center gap-1"
              data-testid="status-audit-feed-refresh"
            >
              <ChevronRight className="w-3 h-3" /> Refresh
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
